import {
  IArticleWorkflow,
  IFormSchemaName
} from '@publishing-engine/domain/models/article-workflow';
import { getSubmissions, getSubmissionsFilters } from './workspace/submission';
import {
  IAdjudicationFormSubmission,
  IAdjudicatorFeedback,
  IAuthorWithEmail,
  IChangeRequestWithSubmissionData,
  IContentManagementSystem,
  ICopyrightForm,
  ICoReview,
  IEditorialFormDataBase,
  IEditorialFormSchema,
  IEditorialPhase,
  IFormDraft,
  IGroupedWorkPhases,
  IUiIntegrityCheckResult,
  IInvitedAdjudicator,
  IInvitedReviewer,
  IJournalPublishingModel,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IPreProduction,
  IReviewer,
  IReviewerFeedback,
  IRevisionRound,
  IRevisionRoundType,
  ISubmissionContext,
  ISubmissionFile,
  ISubmissionRejection,
  ISubmissionWithCaseData,
  ISubmissionWithdrawal,
  ISubmissionWorkflowState,
  ITransferOffer,
  ITransitionGraphResponse,
  IUnsubmit,
  IUser,
  IWorkflowData,
  SubmissionAuthorContribution,
  IWorkspaceFeatureConfig
} from '@publishing-engine/domain/models';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { ISubmissionFieldsResponse } from '../usecases/submission-fields/types';
import { createEndpointRoute } from './endpoint-utils';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import { IArticleTypeWithCompatibility } from '../usecases/article-types/change-article-type';
import { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';

export type PromiseType<T extends Promise<any>> = T extends Promise<infer U>
  ? U
  : never;

export const WorkspaceSubmissionsRoute = createEndpointRoute(
  '/workspaces/:id/submissions'
);
export type IWorkspaceSubmissionsRouteResponse = {
  response: PromiseType<ReturnType<typeof getSubmissions>>;
};
export const WorkspaceWorkflowRoute = createEndpointRoute(
  '/workspace/:id/workflow'
);
export const WorkspaceSubmissionNextRoute = createEndpointRoute(
  '/workspaces/:id/submission/next'
);
export interface WorkspaceWorkflowResponse {
  role: string;
  journalId: string;
  generatedAt: string; // ISO date string
  buckets: WorkflowBucket[];
}

export interface StaticWorkflowSubBucket {
  subBucketId: string;
  subBucketName: string;
  workflowPhases?: string[];
  count: number;
  filter: WorkflowFilter;
  definition?: string;
}

export interface DynamicWorkflowSubBucket {
  subBucketName: string;
  count: number;
  filterValue: string;
  definition?: string;
}

export type WorkflowSubBucket =
  | StaticWorkflowSubBucket
  | DynamicWorkflowSubBucket;

export interface StaticWorkflowBucket {
  bucketId: string;
  bucketName: string;
  totalCount: number;
  isDynamic?: false;
  subBuckets: StaticWorkflowSubBucket[];
}

export interface DynamicWorkflowBucket {
  bucketId: string;
  bucketName: string;
  totalCount?: number;
  isDynamic: true;
  filterName: string;
  subBuckets: DynamicWorkflowSubBucket[];
}

export type WorkflowBucket = StaticWorkflowBucket | DynamicWorkflowBucket;

export type WorkflowFilter = Record<
  string,
  string[] | string | number | boolean | undefined
>;

export interface ITotalSubmissionsResponse {
  totalActiveReviews: number;
  totalCompletedReviews: number;
  totalDraftSubmissions: number;
  totalSubmissions: number;
}

export const MySubmissionsTotalRoute = createEndpointRoute(
  '/organizations/:authRealm/check-activity/:userId'
);

export const WorkspaceSubmissionsFiltersRoute = createEndpointRoute(
  '/workspaces/:id/submissions/filters'
);

export const DashboardMySubmissionsRoute = createEndpointRoute(
  '/dashboard/my-submissions'
);

export const DashboardMySubmissionsTotalsRoute = createEndpointRoute(
  '/dashboard/my-submissions/totals'
);

export const DashboardSubmissionConfigRoute = createEndpointRoute(
  '/dashboard/submission/configs'
);

export type IWorkspaceSubmissionsFiltersRouteResponse = {
  response: PromiseType<ReturnType<typeof getSubmissionsFilters>>;
};

// GET /organizations/:id/submissions/filters
export type MySubmissionsFilters = {
  journals: { id: string; shortname: string; name: string }[];
  workPhases: ISubmissionWorkflowState[];
};

export type IArticleWorkflowResponse = Omit<
  IArticleWorkflow,
  'transitionGraph'
> & {
  transitionGraph: ITransitionGraphResponse;
};

export type IGetArticleWorkflowResponse = {
  articleWorkflow: IArticleWorkflowResponse;
  articleTypeCategoryId: string;
  forms: Partial<Record<IFormSchemaName, IFormSchema>> & {
    authorSubmission: IFormSchema;
  };
};

export type ICommandHandlerResponse = {
  submissionId: string;
  reviewRequestId?: string;
  manuscriptId?: string;
  submission: {
    workflowData: {
      currentDeadline?: string;
      isAdminChecklistDone?: boolean;
    };
  };
};

export type IGetSubmissionContextRoleAssignmentsResponse = {
  role: string;
  users: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    userUnavailability?: IUser['userUnavailability'];
  }[];
}[];

export const GetSubmissionFieldsRoute = createEndpointRoute(
  '/submission/:id/fields'
);

export const GetOrganizationNameRoute = createEndpointRoute(
  '/organization/:id/name'
);

type Author = {
  author_name?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  orcid?: string;
  role?: string;
  raw_affiliation?: string[];
  institution?: string[];
  country?: string[];
};
export type AuthorDetails = Author & {
  contributions?: Array<SubmissionAuthorContribution>;
};
export interface ExtractionResult {
  author_details: Array<AuthorDetails>;
  keywords: Array<string>;
  dataAvailabilityStatement: boolean;
  conflictsOfInterest: string;
  ethicsStatement: boolean;
  consentForPublication: boolean;
  authorContributionStatement: string;
  fundingStatement: string;
  acknowledgement: string;
  correspondingAuthor: IAuthorWithEmail & { status?: IUser['status'] };
}
export interface DocumentExtractedResponse {
  success: boolean;
  submissionId: string;
  extractionResult: ExtractionResult;
}
export const UserOrganizationWorkspacesRoute = createEndpointRoute(
  '/organizations/:id/user/:userId/workspaces'
);

export type IUserOrganizationWorkspacesResponse = {
  collection: {
    organizationId: string;
    workspaceId: string;
    shortname: string;
    name: string;
    logo?: string;
  }[];
  userStatus?: string;
};

export type IGetSubmissionFieldsResponse = {
  organizationId: string;
  submissionId: string;
  submissionContextId: string;
  articleWorkflowId: string;
  articleTypeName: string;
  isFileUpdated?: boolean;
  fields: ISubmissionFieldsResponse;
};

export type IGetSubmissionWorkflowResponse = IGetArticleWorkflowResponse & {
  submissionId: string;
  submissionContextId: string;
  workflowData: IWorkflowData;
  manuscriptId?: string;
  originalManuscriptId?: string;
  revisionNumber?: number;
  cloneSource?: ISubmissionWithCaseData['caseData']['cloneSource'];
  isUADDetected?: boolean;
};

export type IGetSubmissionEditorialAssigneesResponse = {
  submissionId: string;
  submissionContextId: string;
  editorialAssignees: ISubmissionWithCaseData['caseData']['editorialAssignees'];
};

export type IRecommendationAssigneeWithProxy = NonNullable<
  ISubmissionWithCaseData['caseData']['recommendationAssignees']
>[number] & {
  isProxyUser: boolean;
};

export type IGetSubmissionRecommendationAssigneesResponse = {
  submissionId: string;
  submissionContextId: string;
  recommendationAssignees: IRecommendationAssigneeWithProxy[];
};

export type IGetCOCRecommedationSummaryResponse = {
  submissionId: string;
  submissionContextId: string;
  recommendationRounds: ISubmissionWithCaseData['caseData']['recommendationRounds'];
  recommendationWorkflowData:
    | ISubmissionWithCaseData['caseData']['recommendationWorkflowData']
    | null;
  lastRecommendationOrDecision?: string | null;
  lastRecommendationOrDecisionRole?: string | null;
  awaitingUserIds?: string[];
};

export type IGetSubmissionIntegrityReportResponse = {
  submissionId: string;
  submissionContextId: string;
  integrity: ISubmissionWithCaseData['caseData']['submission']['integrity'];
};

export type IUiGetSubmissionIntegrityReportResponse = {
  checkResults: IUiIntegrityCheckResult[];
  checkCount: number;
  failedCheckCount: number;
  submissionId: string;
  roundId: string;
  originalFileUrl: string;
};

export type ISimilarityCheckReportResponse = {
  viewer_url: string;
};
export type IGetSubmissionFeedbackResponse = {
  submissionId: string;
  submissionContextId: string;
  feedback: ISubmissionWithCaseData['caseData']['feedback'];
};
export type IGetPreProductionFeedbackResponse = {
  editorComment: string;
  dateEntered: string;
};

export type IGetSubmissionContextPhasesResponse = {
  phases: IEditorialPhase[];
  grouped: IGroupedWorkPhases;
};

export const GetReviewsSummaryRoute = createEndpointRoute(
  '/submission/:id/feedback/reviews-summary'
);
export type IGetReviewsSummaryResponse = {
  referenceAssessmentId: string;
  reviewId: string;
  comment: string;
  attachments?: ISubmissionFile[];
  recommendation?: string;
}[];

export const GetAdjudicationsSummaryRoute = createEndpointRoute(
  '/submission/:id/feedback/adjudications-summary'
);
export type IGetAdjudicationsSummaryResponse = {
  adjudicationId: string;
  comment: string;
  attachments?: ISubmissionFile[];
  recommendation?: string;
  referenceAssessmentId?: string;
}[];

export const GetActiveRevisionRoute = createEndpointRoute(
  '/submission/:id/feedback/active-revision'
);

export type IGetActiveRevisionResponse = IRevisionRound;

export const GetPreProductionRoute = createEndpointRoute(
  '/submission/:id/feedback/preproduction'
);

export type IGetPreProductionResponse = IPreProduction;

export type IReviewerFeedbacksWithReview = (IReviewerFeedback & {
  reviewAssessment?:
    | IPeerReviewFirstAssessment
    | IPeerReviewRevisionAssessment
    | null;
  reviewAssessmentSchemaId?: string | null;
  reviewer?: IReviewer | null;
  coReview?: ICoReview | null;
})[];

export type IAdjudicatorFeedbacksWithReview = (IAdjudicatorFeedback & {
  reviewAssessment?: IAdjudicationFormSubmission | null;
})[];

export type IDeadlineHistory = {
  grantedDate: string;
  newDeadline: string;
  overdue: string | number;
  deadlineDays: number;
};
export type IReviewerTimeline = {
  originalDueDate: string;
  originalOverdue: string | number;
  reviewId: string;
  totalExtensions: number;
  revisionNumber: string | number;
  deadlineHistory: IDeadlineHistory[];
  invitedDate: string;
  acceptedDate: string;
};
export type IAnonymousComments = {
  feedbackForAuthors: string;
  recommendation: string;
};
export type IGetRevisionHistory = {
  type: IRevisionRoundType;
  dateEntered?: string | null;
  dateCompleted?: string;
  manuscript?: ISubmissionFile;
  reviewerRevisionFeedback?: IReviewerFeedbacksWithReview;
  reviewerOriginalFeedback?: IReviewerFeedbacksWithReview;
  adjudicatorOriginalFeedback?: IAdjudicatorFeedbacksWithReview;
  invitedReviewers?: IInvitedReviewer[];
  invitedAdjudicators?: (IInvitedAdjudicator & {
    role?: 'boardMember' | null;
  })[];
  editorialComment?: string;
  editorialAttachments?: ISubmissionFile[];
  severity?: string;
  authorResponse?: string;
  authorResponseAttachments?: ISubmissionFile[];
  anonymousComments?: IAnonymousComments[];
  timeline?: IReviewerTimeline;
  manuscriptId?: string;
  submissionRoundNumber?: string;
};

export const GetRevisionHistoryRoute = createEndpointRoute(
  '/submission/:id/feedback/revision-history'
);
export type DocumentSchemaResponse = {
  formFields: FormFieldDefinition[];
  id: string;
  organizationId: string;
  schema: unknown;
};
export type IGetRevisionHistoryResponse = {
  documentSchemas: Record<string, DocumentSchemaResponse>;
  current: {
    journalName: string;
    rounds: IGetRevisionHistory[];
    manuscriptId?: string;
  };
  previous: {
    id: string;
    journalName: string;
    manuscriptId?: string;
    rounds: IGetRevisionHistory[];
  }[];
};

export const GetReviewerRoundHistoryRoute = createEndpointRoute(
  '/submission/:id/feedback/reviewer-round-history'
);
export type IGetReviewerRoundHistoryResponse = {
  previousReviews: {
    id: string;
    journalName: string;
    transferDate?: string;
    rounds: IGetRevisionHistory[];
  }[];
};

export const GetSubmissionExtensionDeadlineRoute = createEndpointRoute(
  '/submission/:id/deadline'
);
export type IGetExtensionDeadlineResponse = {
  currentState: string;
  currentDeadline?: Date;
  nextDeadline: Date | null;
  deadlineExtensionInDays?: number;
  deadlineExtensions?: IWorkflowData['deadlineExtensions'];
};

export const TransferNetworkConfigRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/transfer-networks/config'
);
export type IGetTransferNetworkConfigResponse = {
  id: string;
  journalName: string;
  articleTypes: string[];
  enabled: boolean;
}[];

export const GetSubmissionContextCMSRoute = createEndpointRoute(
  '/submission-contexts/:id/cms'
);
export type IGetSubmissionContextCMSResponse =
  | IContentManagementSystem
  | undefined;

export const JournalSettingRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/settings'
);

export const JournalConfigurationBucketsRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/configuration/buckets'
);

export const JournalConfigurationDefaultChecklistRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/configuration/default-checklist'
);

export const JournalConfigurationArticleChecklistRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/configuration/article/checklist'
);

export const JournalConfigurationDefaultBucketsRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/configuration/default-buckets'
);

export const JournalConfigurationDecisionTemplatesRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/configuration/decision-templates'
);

export const JournalConfigurationDefaultDecisionTemplateOptionsRoute =
  createEndpointRoute(
    '/submission-contexts/:ctxId/configuration/default-decision-template-options'
  );

export const SubmissionDecisionTemplatesByTypeRoute = createEndpointRoute(
  '/submissions/:id/decision-templates'
);

export const ArticleWorkflowSubmissionStatesRoute = createEndpointRoute(
  '/article-types/:id/submission-states'
);
export type IGetArticleWorkflowSubmissionStatesResponse = {
  id: string;
  states: ISubmissionWFState[];
};

export const SubmissionTransferNetworkRoute = createEndpointRoute(
  '/submissions/:id/transfer-networks'
);

export type ITransferNetworkArticleType = {
  articleWorkflowId: string;
  formSchemas: Record<string, string>;
  name: string;
  description: string;
  articleTypeCategoryId: string;
};

export type IGetSubmissionTransferNetworkResponse = {
  id: string;
  journalName: string;
  publishingModel: IJournalPublishingModel;
  articleTypes: ITransferNetworkArticleType[];
}[];

export const GetTransferOfferRoute = createEndpointRoute(
  '/submission/:id/feedback/transfer-offer'
);
export type IGetTransferOfferResponse = Omit<
  ITransferOffer,
  'targetJournals'
> & {
  targetJournals: {
    id: string;
    name: string;
    articleTypeCategoryId: string;
    articleWorkflowId: string | null;
    articleTypeName: string;
    publishingModel: IJournalPublishingModel;
    url: string;
  }[];
};

export const GetMissingFieldsFromArticleIdRoute = createEndpointRoute(
  '/submission/:id/missing-fields'
);
export type IGetMissingFieldsFromArticleIdResponse = {
  submissionId: string;
  articleWorkflowId: string;
  fields: ISubmissionFieldsResponse;
  hasMissingFields: boolean;
};

export const FormDraftRoute = createEndpointRoute('/form-drafts/:entityId');
export type IGetFormDraftResponse = Omit<
  IFormDraft,
  'formSchemaId' | 'formType'
>;

export type FocusIssues = {
  id: string;
  name: string;
  issueId: string;
  issueLink: string;
  status: string;
};
export const SubmissionRejectionRoute = createEndpointRoute(
  '/submissions/:id/rejections'
);
export type IGetSubmissionRejectionResponse = ISubmissionRejection | undefined;

export const SubmissionWithdrawalRoute = createEndpointRoute(
  '/submissions/:id/withdrawals'
);
export type IGetSubmissionWithdrawalResponse =
  | ISubmissionWithdrawal
  | undefined;

export const SubmissionChangeRequestRoute = createEndpointRoute(
  '/submissions/:id/change-requests'
);

export type IGetSubmissionChangeRequestResponse = {
  changeRequest: IChangeRequestWithSubmissionData;
  totalAddressedRequests: number;
};

export const JournalsSearchRoute = createEndpointRoute(
  '/submission-contexts/search'
);
export type JournalsSearchResponse = {
  total: number;
  collection: ISubmissionContext[];
};

export const UsersSearchRoute = createEndpointRoute('/users/search');
export type UsersSearchResponse = {
  total: number;
  collection: Pick<IUser, 'id' | 'firstName' | 'lastName' | 'email'>[];
};

export const UsersListRoute = createEndpointRoute('/users/:id/list');
export type UsersListResponse = {
  total: number;
  items: Pick<IUser, 'id' | 'firstName' | 'lastName' | 'email'>[];
};

export const SubmissionRequestedChanges = createEndpointRoute(
  '/submissions/:id/change-requests/fields'
);
export type IGetSubmissionRequestedChanges =
  | IGetSubmissionFieldsResponse
  | undefined;

export const SubmissionUnsubmitRoute = createEndpointRoute(
  '/submissions/:id/unsubmits'
);
export type IGetSubmissionUnsubmitResponse = IUnsubmit | undefined;

export const SubmissionCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/copyright-form'
);

export const SubmittingAuthorOnlyCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/author/copyright-form'
);

export type IGetSubmissionCopyrightFormResponse = ICopyrightForm | undefined;

export type IGetSubmissionCoAuthorCopyrightFormResponse = {
  isTokenExpired: boolean;
  isFormSubmitted: boolean;
  publisherName?: string;
  journalName?: string;
  submissionTitle?: string;
  form?: {
    formFields: FormFieldDefinition[];
    id: string;
    organizationId: string;
    schema: unknown;
  };
  downloadUrl?: string;
  date?: string;
};

export const HasOwnedSubmissionInWorkspaceRoute = createEndpointRoute(
  '/workspaces/:workspaceId/users/:userId/has-owned-submissions'
);

export const WorkspaceMembershipIdRoute = createEndpointRoute(
  '/workspaces/:id/memberships/:membershipId'
);

export type IGetWorkspaceMembershipResponse =
  | {
      userId: string;
      workspace: {
        id: string;
        shortname: string;
      };
      organization: {
        id: string;
        name: string;
      };
    }
  | undefined;

export type IRemindersConfigRouteResponse = {
  type: 'submission' | 'review' | 'adjudication' | 'earlyAssessment';
  phase: string;
  displayName: string;
  enabled?: boolean;
  reminderRounds: number[]; // reminder rounds e-g [1,2,3] as 1st 2nd and 3rd
  reminderRoundDays: number[];
};

export const ReminderConfigsRoute = createEndpointRoute(
  '/organizations/:id/reminder-configs'
);

export type IRemindersJournalConfigRouteResponse =
  IRemindersConfigRouteResponse & {
    reminderInDays?: number[];
  };
export const ReminderConfigsRouteForJournals = createEndpointRoute(
  '/organizations/:id/submission-context/:ctxId/reminder-configs'
);

/**
 * Endpoint for Email events with sub events/type e-g Reject with Rejection Reason
 */
export const EventsWithSubEventsRoute = createEndpointRoute(
  '/organizations/:id/email-events'
);

type IEventWithSubEvent = {
  event: string;
  eventKey: string;
  eventDisplayName: string;
  subEventCategory?: 'subtype' | 'action';
  personalizable?: boolean;
  subEventValue?: string;
  description?: string;
};
export type IEventsWithSubEventsResponse = IEventWithSubEvent[];

/**
 * Endpoint to fetch emails for an event
 */
export const EventEmailsRoute = createEndpointRoute(
  '/submissions/:submissionId/emails/event/:eventId'
);

/**
 * Endpoint for Organization based interaction command  endpoint
 */
export const OrganizationCommandInteractionRoute = createEndpointRoute(
  '/organizations/command/interact'
);

export type IAddWorkspaceMembershipResponse = {
  successCount: number;
  failureCount: number;
};

export const ChecklistRoute = createEndpointRoute(`/submissions/:id/checklist`);
export type IChecklistResponse = {
  editorialFormSchema: IEditorialFormSchema;
  editorialFormData: IEditorialFormDataBase;
};

export const JournalMembershipRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/memberships'
);

export const MyJournalMembershipRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/memberships/me'
);

export const UnassignedJournalMembershipRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/memberships/unassigned'
);

export const ReviewDeclineRoute = createEndpointRoute(
  '/reviews/:id/review/decline'
);

export const ReviewDeclineInvitationRoute = createEndpointRoute(
  '/reviews/:id/review/decline-invitation'
);

export const CoReviewDeclineRoute = createEndpointRoute(
  '/reviews/:id/co-review/decline'
);

export const CoReviewInvitationRoute = createEndpointRoute(
  '/reviews/:id/co-review/invitation'
);
export const CoReviewDeclineReasonRoute = createEndpointRoute(
  '/reviews/:id/co-review/invitation/declineReason'
);

export const SubmissionArticleTypesCompatibilityRoute = createEndpointRoute(
  '/submission/:id/article-types-compatibility'
);

export const SubmissionVersionHistoryRoute = createEndpointRoute(
  '/submission/:id/version-log'
);

export const SubmissionAuthorCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/author/copyright-form'
);
export const SubmissionAuthorICMJEFormRoute = createEndpointRoute(
  '/submission/:id/author/icmje-form'
);

export const SubmissionAuthorCopyrightFormListRoute = createEndpointRoute(
  '/submission/:id/copyright-form-list'
);

export const SubmissionAuthorICMJEFormListRoute = createEndpointRoute(
  '/submission/:id/icmje-form-list'
);

export const SubmissionAuthorSubmitAllCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/copyright-forms/submit'
);

export const SubmissionAuthorSubmitAllICMJEFormRoute = createEndpointRoute(
  '/submission/:id/icmje-forms/submit'
);

export const SubmissionEditorDownloadCopyrightFormsRoute = createEndpointRoute(
  '/submission/:id/copyright-forms/download'
);

export const SubmissionEditorDownloadICMJEFormsRoute = createEndpointRoute(
  '/submission/:id/icmje-forms/download'
);

export const SubmissionEditorSendCopyrightFormEmailRoute = createEndpointRoute(
  '/submission/:id/copyright-form/send-email'
);
export const SubmissionEditorSendICMJEFormEmailRoute = createEndpointRoute(
  '/submission/:id/icmje-form/send-email'
);

export const SubmissionCoAuthorCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/co-author/copyright-form'
);

export const SubmissionCoAuthorSubmitCopyrightFormRoute = createEndpointRoute(
  '/submission/:id/co-author/copyright-form/submit'
);

export type ISubmissionArticleTypesCompatibilityRouteResponse =
  IArticleTypeWithCompatibility[];

export const MyWorkspacesRoute = createEndpointRoute(
  '/workspaces/my-workspaces'
);

export const VendorExportHistoryRoute = createEndpointRoute(
  '/submission/:submissionId/vendorExport/history'
);
export const VendorExportRetryRoute = createEndpointRoute(
  '/submission/:submissionId/vendorExport/retry'
);
export const VendorExportConnectionTest = createEndpointRoute(
  '/submission/vendorExport/testConnection'
);
export const VendorExportDownload = createEndpointRoute(
  '/submission-context/:ctxId/submission/:submissionId/vendorExport/download'
);

export type IMyWorkspacesRouteResponse = {
  collection: {
    organizationId: string;
    workspaceId: string;
    shortname: string;
    name: string;
    logo?: string;
    featureConfig?: IWorkspaceFeatureConfig;
  }[];
  userStatus?: string;
};

export const WorkspaceMembershipsRoute = createEndpointRoute(
  '/workspaces/:id/memberships'
);

export const WorkspaceGrantOrgAdminRoute = createEndpointRoute(
  '/workspaces/:id/grant-org-admin'
);

export const WorkspaceRevertOrgAdminRoute = createEndpointRoute(
  '/workspaces/:id/revert-org-admin'
);

export const WorkspaceMembershipRoute = createEndpointRoute(
  '/workspaces/memberships/:membershipId'
);

export const WorkspaceMergeAccountSearchRoute = createEndpointRoute(
  '/workspaces/:id/merge-account-search'
);

export const WorkspaceMergeAccountSearchFieldsRoute = createEndpointRoute(
  '/workspaces/:id/merge-account-search/fields'
);

export const WorkspaceMergeAccountsRoute = createEndpointRoute(
  '/workspaces/:id/merge-accounts'
);

export const WorkspaceMergeAccountsUndoRoute = createEndpointRoute(
  '/workspaces/:id/merge-accounts/undo'
);

export const WorkspaceByShortnameRoute = createEndpointRoute(
  '/workspaces/:shortname'
);

export const WorkspaceRolesRoute = createEndpointRoute('/workspaces/:id/roles');

export const WorkspaceRoleNameRoute = createEndpointRoute(
  '/workspaces/:id/roles/:roleName'
);

export const OrganizationArticleTypesRoute = createEndpointRoute(
  '/organizations/:id/article-types'
);

export const OrganizationMyActivityRoute = createEndpointRoute(
  '/organizations/:shortname/my-recent-activity/:userId'
);

export const OrganizationCheckActivityRoute = createEndpointRoute(
  '/organizations/:shortname/check-activity/:userId'
);

export const WorkspaceTagsRoute = createEndpointRoute('/workspaces/:id/tags');

export const WorkspaceTagRoute = createEndpointRoute(
  '/workspaces/:id/tags/:tagId'
);

export const WorkspaceSavedFiltersRoute = createEndpointRoute(
  '/workspaces/:id/saved-filters'
);

export const WorkspaceSavedFilterRoute = createEndpointRoute(
  '/workspaces/:id/saved-filters/:sfid'
);

export const DashboardSavedFiltersRoute = createEndpointRoute(
  '/dashboard/saved-filters'
);

export const DashboardSavedFilterRoute = createEndpointRoute(
  '/dashboard/saved-filters/:sfid'
);

export const WorkspaceJournalsRoute = createEndpointRoute(
  '/workspaces/:id/journals'
);

export const WorkspaceEditorialPhasesRoute = createEndpointRoute(
  '/workspaces/:id/phases'
);

export const HasCompletedReviewsRoute = createEndpointRoute(
  '/submission/:id/has-completed-reviews'
);

export const AdjudicatorEligibilityRoute = createEndpointRoute(
  '/submissions/:id/adjudicators-eligibility-checks'
);

export const AuthRealmJournalsRoute = createEndpointRoute(
  '/auth-realms/:id/journals'
);

export const SubmissionOverviewRoute = createEndpointRoute(
  '/submission/:id/:manuscritpId/overview-details'
);

export const SubmissionTransferOverviewRoute = createEndpointRoute(
  '/submission/:id/:manuscritpId/transfer-overview-details'
);

export const ReportsConfigRoute = createEndpointRoute('/reports/configs');
export const ReportsGenerateRoute = createEndpointRoute(
  '/reports/generate/:reportId'
);

export const AnalyticsConfigRoute = createEndpointRoute('/analytics/configs');
export const AnalyticsGenerateRoute = createEndpointRoute(
  '/analytics/:analyticTabId'
);

export const SubmissionContextTagsRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/tags'
);
export const ContactUsRoute = createEndpointRoute(`/contact-us`);
export const UnverifiedUserContactUsRoute =
  createEndpointRoute('/user/contact-us');
export const ContactUsConfigRoute = createEndpointRoute(
  '/contact-us/categories'
);
export const ContactUsUnauthenticatedConfigRoute = createEndpointRoute(
  '/contact-us/user/categories'
);

export const NotesHistoryRoute = createEndpointRoute(
  '/submission-contexts/:ctxId/notes/history'
);
export type INotesHistoryGroup = {
  submissionContextId: string;
  journalName: string;
  transferDate?: string;
  notes: ISubmissionNotesListItem[];
};
export type INotesHistoryResponse = {
  history: INotesHistoryGroup[];
};

export const UADReportRoute = createEndpointRoute(
  '/submission/:id/uad-reports'
);
export const AcknowledgeUADReportRoute = createEndpointRoute(
  '/submission/:id/acknowledge/uad-reports'
);
export const AcknowledgedUADReportsRoute = createEndpointRoute(
  '/submission/:id/acknowledged/uad-reports'
);
export const SubmissionParticipantsEmailStatusRoute = createEndpointRoute(
  '/submission/:id/participants-email-status'
);
export const CrossServiceSubmissionMetadataRoute = createEndpointRoute(
  '/cross-service/submission/metadata'
);
export const DownloadUADReportRoute = createEndpointRoute(
  '/submission/:id/download/uad-reports'
);
export const DownloadEmailStatusReportRoute = createEndpointRoute(
  '/submission/:id/download/email-status-report'
);

export const SendEmailNotification = createEndpointRoute(
  '/submission/:id/send-notification'
);

export const GetSubmittedReviewersRoute = createEndpointRoute(
  '/submission/:id/feedback/submitted-reviewers'
);

export const PageHelpRoute = createEndpointRoute('/pageHelp');
export const PageHelpCreateRoute = createEndpointRoute('/pageHelp/:pageName');
export const PageHelpTopicRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics/:topicId'
);
export const PageHelpCreateTopicRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics'
);
export const PageHelpVideoRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics/:topicId/videos'
);
export const PageHelpFAQRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics/:topicId/faqs'
);
export const PageHelpFAQIDRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics/:topicId/faqs/:faqId'
);
export const PageHelpVideoIDRoute = createEndpointRoute(
  '/pageHelp/:pageName/topics/:topicId/videos/:videoId'
);
export const PageHelpPlayVideoRoute = createEndpointRoute(
  '/pageHelp/play/:pageName/topics/:topicId'
);

export interface ISubmittedReviewerDetails {
  reviewId: string;
  reviewerId: string;
  reviewerName: string;
  email: string;
  recommendation: string;
  reviewedOn?: string;
}

export type IGetSubmittedReviewersResponse = ISubmittedReviewerDetails[];
